import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useKeycloak } from '@react-keycloak/web';
import { useAdmin } from './context/AdminContext';
import { BYPASS_AUTH } from './utils/config.js';

// Safe hook: returns mock auth state when bypass is enabled
const useSafeKeycloak = () => {
  if (BYPASS_AUTH) {
    return { initialized: true, keycloak: { authenticated: true, login: () => {}, logout: () => {} } };
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useKeycloak();
};

// Import components
import LoginPage from './components/LoginPage';
import ResearchDashboard from './components/ResearchDashboard';
import DashboardPage from './components/DashboardPage';
import PrivateRoute from './components/PrivateRoute';
import SchedulesPage from './components/SchedulesPage';
import TeamSettings from './components/TeamSettings';
import VerifyInvitePage from './components/VerifyInvitePage';
import NoAccess from './components/noAccess';
import AgentsPage from './components/AgentsPage';
import './index.css';
import { ToastContainer } from 'react-toastify';
import IntegrationsPage from './components/integrations/IntegrationsPage';

function App() {
  const { initialized, keycloak } = useSafeKeycloak();

  // AdminContext handles all authorization + integration state via a single /api/me call
  const { isAdmin, isLoading } = useAdmin();

  // Safety net: if still loading after 10 seconds, show a retry button so the
  // user is never permanently stuck on the spinner.
  const [loadingTimedOut, setLoadingTimedOut] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => setLoadingTimedOut(true), 10000);
    return () => clearTimeout(t);
  }, []);

  // Show loading only while Keycloak is initializing OR while admin data is loading for an authenticated user.
  // With no onLoad, Keycloak initializes instantly without network calls.
  if (!initialized || (keycloak.authenticated && isLoading)) {
    if (loadingTimedOut) {
      return (
        <div className="flex flex-col justify-center items-center min-h-screen bg-gray-100 gap-4">
          <p className="text-gray-600 text-sm">Authentication is taking longer than expected.</p>
          <button
            onClick={() => {
              sessionStorage.clear();
              localStorage.removeItem('me_cache');
              window.location.href = window.location.origin;
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
          >
            Retry Login
          </button>
        </div>
      );
    }
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-3 text-gray-600">
          {!initialized ? 'Initializing authentication...' : 'Loading...'}
        </p>
      </div>
    );
  }

  return (
    <>
      <Router>
        <Toaster position="top-right" />
        <Routes>
          <Route path="/verify-team-invite" element={<VerifyInvitePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/no-access" element={<NoAccess />} />

          <Route
            path="/dashboards"
            element={
              <PrivateRoute>
                <DashboardPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard/:dashboardId"
            element={
              <PrivateRoute>
                <ResearchDashboard />
              </PrivateRoute>
            }
          />

          <Route
            path="/schedules"
            element={
              <PrivateRoute>
                <SchedulesPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/agents"
            element={
              <PrivateRoute>
                {isAdmin ? (
                  <AgentsPage />
                ) : (
                  <Navigate to="/dashboards" replace />
                )}
              </PrivateRoute>
            }
          />

          <Route
            path="/team-settings"
            element={
              <PrivateRoute>
                <TeamSettings />
              </PrivateRoute>
            }
          />

          <Route
            path="/integrations"
            element={
              <PrivateRoute>
                <IntegrationsPage />
              </PrivateRoute>
            }
          />

          <Route path="/admin" element={<Navigate to="/dashboards" replace />} />

          <Route path="/" element={<Navigate to="/dashboards" replace />} />
          <Route path="*" element={<Navigate to="/dashboards" replace />} />
        </Routes>
        <ToastContainer />
      </Router>
    </>
  );
}

export default App;